import React from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserTie,
  faWallet,
  faArrowRight,
  faCheckCircle,
  faMoneyBillWave,
  faIdCard,
  faCertificate,
  faUserPlus,
  faChevronRight,
  faRocket,
  faClipboardCheck,
  faCoins,
  faMoneyBillWaveAlt,
  faUsers,
  faGraduationCap,
  faHandHoldingDollar,
  faGift,
  faChartLine,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";

const Badge = ({ color = "purple", children }) => {
    const colors = {
        purple: "bg-purple-600/20 text-purple-300",
        green: "bg-green-600/20 text-green-300",
        blue: "bg-blue-600/20 text-blue-300",
        amber: "bg-amber-600/20 text-amber-300",
        red: "bg-red-600/20 text-red-300"
    };

    return (
        <span
            className={`px-2 py-1 rounded-full text-[10px] lg:text-xs ${colors[color]}`}
        >
            {children}
        </span>
    );
};

const FlowStep = ({ icon, title, children }) => (
    <div className="flex gap-3">
        <div className="flex flex-col items-center">
            <div className="h-9 w-9 rounded-full bg-purple-700 flex items-center justify-center">
                {icon}
            </div>
            <div className="flex-1 w-px bg-gray-700 mt-1"></div>
        </div>

        <div className="flex-1 pb-5">
            <h3 className="font-semibold text-white text-xs lg:text-sm">
                {title}
            </h3>

            <div className="mt-2 text-gray-300 text-xs lg:text-sm leading-relaxed">
                {children}
            </div>
        </div>
    </div>
);

const SectionCard = ({ title, icon, children }) => (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-3 shadow">
        <div className="flex items-center gap-2 mb-3">
            <div className="text-purple-400">{icon}</div>

            <h2 className="text-white font-semibold text-sm lg:text-base">
                {title}
            </h2>
        </div>

        {children}
    </div>
);

const InfoBox = ({ children }) => (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 text-gray-200 text-xs lg:text-sm">
        {children}
    </div>
);

export default function PDBizFlow() {
    return (
        <div className="max-w-5xl mx-auto text-white px-2 lg:px-4 py-3 space-y-4 border-t-2 border-blue-400 rounded-2xl">
            <div className="sticky top-0 z-30 bg-gray-950 border border-gray-800 rounded-xl p-3">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="font-bold text-sm lg:text-lg">
                            PurpleDoor DMD → DC → Subscriber / Gifter Business Flow
                        </h1>
                        <p className="text-gray-400 text-xs lg:text-sm mt-1">
                            India and Overseas (Export) • Director • Coordinator • Subscriber • FuelStash
                        </p>
                    </div>
                    <Badge color="purple">
                        BizFlow
                    </Badge>
                </div>
            </div>

            <SectionCard
                title="Business Journey"
                icon={<FontAwesomeIcon icon={faRocket}/>}
            >
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                    {[
                        "Getting Started",
                        "Director",
                        "Coordinator",
                        "Subscribers"
                    ].map((x) => (
                        <div
                            key={x}
                            className="bg-gray-800 rounded-lg p-3 text-center"
                        >
                            <div className="text-purple-400 mb-2">
                                <FontAwesomeIcon icon={faChevronRight} className="mx-auto"/>
                            </div>
                            <div className="text-xs lg:text-sm">
                                {x}
                            </div>
                        </div>
                    ))}
                </div>
            </SectionCard>

            <SectionCard
                title="Getting Started"
                icon={<FontAwesomeIcon icon={faUserPlus} />}
            >
                <FlowStep
                    icon={<FontAwesomeIcon icon={faUserPlus} />}
                    title="Register on Think5Whys"
                >
                    Create your Think5Whys account.
                    <div className="mt-2 flex flex-wrap gap-2">
                        <Badge color="green">
                            14 Character Code Generated
                        </Badge>

                        <Badge color="blue">
                            Permanent Identity
                        </Badge>
                    </div>
                </FlowStep>

                <FlowStep
                    icon={<FontAwesomeIcon icon={faIdCard} />}
                    title="Complete Profile"
                >
                    Fill your PurpleDoor Profile (PDProfile).
                    Verify identity using SnapID.
                    Choose your Country carefully.
                    <InfoBox>
                        Country selection is permanent and cannot be changed later under any circumstances.
                    </InfoBox>
                </FlowStep>
            </SectionCard>

            {/* Director */}
            <SectionCard
                title="Become a Director (DMD)"
                icon={<FontAwesomeIcon icon={faUserTie} />}
            >
                <FlowStep
                    icon={<FontAwesomeIcon icon={faMoneyBillWaveAlt} />}
                    title="Commitment Fee & Payment"
                >
                    <div className="grid grid-cols-3 gap-2 mt-2">
                        <div className="bg-gray-800 rounded-lg p-2 text-center">
                            <div className="text-gray-400 text-[10px]">
                                Fee
                            </div>
                            <div className="font-bold">
                                ₹2,999
                            </div>
                        </div>

                        <div className="bg-gray-800 rounded-lg p-2 text-center">
                            <div className="text-gray-400 text-[10px]">
                                GST
                            </div>
                            <div className="font-bold">
                                ₹540
                            </div>
                        </div>

                        <div className="bg-purple-800 rounded-lg p-2 text-center">
                            <div className="text-[10px]">
                                Total
                            </div>
                            <div className="font-bold">
                                ₹3,539
                            </div>
                        </div>
                    </div>
                    <p className="text-xs text-gray-400 mt-3">Paid directly to Company via UPI / Net Banking</p>
                </FlowStep>

                <FlowStep
                    icon={<FontAwesomeIcon icon={faWallet} />}
                    title="FuelStash Credits"
                >
                    Company credits <span className="font-semibold">₹3,539 + ₹3,539 = 7,078</span> FuelStash Credits
                    <div className="mt-3 space-y-2">
                        <div className="flex justify-between bg-gray-800 rounded-lg p-2">
                            <span>Director Subscription</span>
                            <span>3,539</span>
                        </div>
                        <div className="flex justify-between bg-green-900/30 border border-green-700 rounded-lg p-2">
                            <span>Working Capital</span>
                            <span className="text-green-300 font-semibold">3,539</span>
                        </div>
                    </div>
                </FlowStep>

                <FlowStep
                    icon={<FontAwesomeIcon icon={faClipboardCheck} />}
                    title="Activation"
                >
                    <ul className="list-disc ml-4 space-y-1">
                        <li className="text-xs lg:text-sm text-gray-100">Director Subscription activated for 1 year</li>
                        <li className="text-xs lg:text-sm text-gray-100">Director Status Activated by Company</li>
                        <li className="text-xs lg:text-sm text-gray-100">DMD Agreement Available Online</li>
                        <li className="text-xs lg:text-sm text-gray-100">Golden Frame Certificate Available after Orientation</li>
                    </ul>
                </FlowStep>
            </SectionCard>

            <SectionCard
                title="Director Snapshot"
                icon={<FontAwesomeIcon icon={faCoins} />}
            >
                <div className="grid grid-cols-2 gap-2">
                    <div className="bg-gray-800 rounded-lg p-3">
                        <div className="text-gray-400 text-[10px]">Subscription</div>
                        <div className="font-semibold mt-1">1 Year</div>
                    </div>
                    <div className="bg-gray-800 rounded-lg p-3">
                        <div className="text-gray-400 text-[10px]">Wallet Balance</div>
                        <div className="font-semibold mt-1 text-green-300">3,539 Credits</div>
                    </div>
                    <div className="bg-gray-800 rounded-lg p-3">
                        <div className="text-gray-400 text-[10px]">Certificate</div>
                        <div className="font-semibold mt-1">After Orientation</div>
                    </div>
                    <div className="bg-gray-800 rounded-lg p-3">
                        <div className="text-gray-400 text-[10px]">Identity</div>
                        <div className="font-semibold mt-1">SnapID Verified</div>
                    </div>
                </div>
            </SectionCard>

            {/* Coordinator */}
            <SectionCard
                title="Enroll a Coordinator (DC)"
                icon={<FontAwesomeIcon icon={faUsers} />}
            >
                <FlowStep
                    icon={<FontAwesomeIcon icon={faMoneyBillWave} />}
                    title="Commitment Fee"
                >
                    ₹1,499 + 18% GST = <span className="font-bold">₹1,769</span><br />
                    Paid to sponsoring DMD (Cash / UPI / Net Banking)
                </FlowStep>

                <FlowStep
                    icon={<FontAwesomeIcon icon={faWallet} />}
                    title="FuelStash & Activation"
                >
                    <div className="space-y-3">
                        <div>DMD transfers 1,769 FuelStash Credits to DC</div>
                        <div>DC purchases Coordinator Subscription</div>
                        <div>DMD activates DC → Informs Company</div>
                        <div className="bg-green-900/30 border border-green-700 rounded-lg p-3">
                            Company transfers additional 1,769 FuelStash Credits as working capital
                        </div>
                    </div>
                </FlowStep>

                <FlowStep
                    icon={<FontAwesomeIcon icon={faClipboardCheck} />}
                    title="DMD Commission"
                >
                    DMD earns 20% commission (₹300) + GST handling as per policy
                </FlowStep>
            </SectionCard>

            {/* Orientation */}
            <SectionCard
                title="DMD & DC Orientation"
                icon={<FontAwesomeIcon icon={faGraduationCap} />}
            >
                <FlowStep
                    icon={<FontAwesomeIcon icon={faCheckCircle} />}
                    title="3-Day Self-Paced Program"
                >
                    Includes self-learning modules, two live online sessions (≈1 hour each) conducted by Founder / Authorized Mentor.
                    <div className="mt-3">
                        <Badge color="green">Certified Digital Franchise</Badge>
                    </div>
                    <InfoBox>
                        The orientation itself delivers exceptional value applicable in personal and professional life.
                    </InfoBox>
                </FlowStep>
            </SectionCard>

            {/* Subscriber */}
            <SectionCard
                title="Subscriber Acquisition"
                icon={<FontAwesomeIcon icon={faUserPlus} />}
            >
                <FlowStep
                    icon={<FontAwesomeIcon icon={faMoneyBillWave} />}
                    title="Subscription Purchase"
                >
                    Subscriber pays <span className="font-bold">₹99 + 18% GST = ₹117</span> to DC/DMD.
                    <div className="mt-2">DC/DMD transfers 117 FuelStash Credits to Subscriber.</div>
                </FlowStep>

                <FlowStep
                    icon={<FontAwesomeIcon icon={faClipboardCheck} />}
                    title="Activation & Benefits"
                >
                    • Subscription active for 90 days<br />
                    • Access to PurpleDoor content<br />
                    • Invoice available in dashboard
                </FlowStep>
            </SectionCard>

            {/* Working Capital */}
            <SectionCard
                title="Working Capital Flow"
                icon={<FontAwesomeIcon icon={faHandHoldingDollar} />}
            >
                <FlowStep
                    icon={<FontAwesomeIcon icon={faWallet} />}
                    title="DC Working Capital"
                >
                    DC retains 10–35% of revenue (mutual agreement).<br />
                    Remaining shared with sponsoring DMD.
                </FlowStep>

                <FlowStep
                    icon={<FontAwesomeIcon icon={faWallet} />}
                    title="DMD Wallet Recharge"
                >
                    When credits are low, DMD pays Company (min ₹1,000 + GST).<br />
                    Company tops up equivalent FuelStash Credits.
                </FlowStep>
            </SectionCard>

            {/* Margin Distribution */}
            <SectionCard
                title="Margin Distribution Policy"
                icon={<FontAwesomeIcon icon={faChartLine} />}
            >
                <InfoBox>
                    Every sale carries <strong>40% margin</strong> (GST excluded).<br /><br />
                    Default split:<br />
                    • DMD: 10%<br />
                    • DC: 10%<br />
                    • Influencer: 10%<br />
                    • Miscellaneous Pool: 10%
                </InfoBox>
                <p className="text-xs text-gray-400 mt-3">
                    Sponsoring DMD has full flexibility for internal distribution via personal collection or FuelStash transfers.
                </p>
            </SectionCard>

            {/* Gifting Flow */}
            <SectionCard
                title="Gifting Flow"
                icon={<FontAwesomeIcon icon={faGift} />}
            >
                <FlowStep
                    icon={<FontAwesomeIcon icon={faGift} />}
                    title="Complete Gifting Process"
                >
                    1. Gifter pays Product Price + GST to DMD/DC<br />
                    2. DMD/DC transfers FuelStash Credits to Gifter<br />
                    3. Gifter selects "Gift It" → Enters Recipient Code<br />
                    4. Gift Coupon generated and shared<br />
                    5. Recipient redeems coupon → Subscription activated
                </FlowStep>
                <div className="mt-3">
                    <Badge color="amber">No expiry on Gift Coupon</Badge>
                </div>
            </SectionCard>

            {/* Recovery Illustrations */}
            <SectionCard
                title="Recovery & Growth Illustrations"
                icon={<FontAwesomeIcon icon={faChartLine} />}
            >
                {/* DMD Recovery */}
                <div className="mb-6">
                    <h4 className="font-semibold text-amber-300 mb-2">DMD Recovery</h4>
                    <div className="bg-gray-800 rounded-lg p-3 text-xs space-y-2">
                        <div><strong>Initial Investment:</strong> ₹3,539</div>
                        <div><strong>Working Capital:</strong> 3,539 Credits</div>
                        <div className="pt-2 border-t border-gray-700">
                            Direct Sales (₹39.60 margin per sub): ~90 subscribers to recover commitment fee equivalent
                        </div>
                    </div>
                </div>

                {/* DC Recovery */}
                <div>
                    <h4 className="font-semibold text-amber-300 mb-2">DC Recovery</h4>
                    <div className="bg-gray-800 rounded-lg p-3 text-xs space-y-2">
                        <div><strong>Initial Investment:</strong> ₹1,769</div>
                        <div><strong>Working Capital:</strong> 1,769 Credits</div>
                        <div className="pt-2 border-t border-gray-700">
                            At ₹35/share: ~51 subscribers<br />
                            At ₹20/share: ~89 subscribers
                        </div>
                    </div>
                </div>
            </SectionCard>

            {/* Scaling Illustration */}
            <SectionCard
                title="Business Scaling Illustration"
                icon={<FontAwesomeIcon icon={faUsers} />}
            >
                <div className="text-xs bg-gray-800 rounded-lg p-4">
                    <div className="mb-3">Example: DMD activates 10 DCs × 50 subscribers each = 500 subscribers</div>
                    <div className="text-green-300">Potential earnings depend on internal sharing arrangements (e.g., ₹10–35 per subscriber)</div>
                </div>
            </SectionCard>

            {/* Important Note */}
            <SectionCard
                title="Important Note"
                icon={<FontAwesomeIcon icon={faExclamationTriangle} />}
            >
                <InfoBox>
                    PurpleDoor does not guarantee any income, profit, recovery, earnings, or return on investment. 
                    All figures are purely illustrative. Actual results depend on sales volume, customer retention, 
                    market conditions, personal effort, and mutually agreed arrangements.
                </InfoBox>
            </SectionCard>

            <div className="text-center text-[10px] text-gray-500 pt-4">
                FuelStash: 1 Credit = ₹1 (India) | Unit 1 (Overseas)
            </div>
        </div>
    );
}